import React, { useState } from "react";
import { Container, Card, Button, Alert, Form } from "react-bootstrap";
import { useAuth } from "../../../contexts/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getSafeReturnTo } from "./authRedirect";

const Signup = () => {
  const { signUpWithEmail, signInWithGoogle, signInWithGithub, error } =
    useAuth();
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getSafeReturnTo(location.search, "/verify-email");

  const handleEmailSignup = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setLocalError("Passwords do not match");
    }

    if (password.length < 6) {
      return setLocalError("Password must be at least 6 characters");
    }

    try {
      setLocalError("");
      setLoading(true);
      await signUpWithEmail(email, password, displayName);
      navigate(`/verify-email?returnTo=${encodeURIComponent(returnTo)}`);
    } catch (error) {
      // Error already set in AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLocalError("");
      setLoading(true);
      await signInWithGoogle();
      navigate(returnTo);
    } catch (error) {
      if (error.code !== "auth/account-exists-with-different-credential") {
        setLocalError("Failed to sign up with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGithubSignIn = async () => {
    try {
      setLocalError("");
      setLoading(true);
      await signInWithGithub();
      navigate(returnTo);
    } catch (error) {
      if (error.code !== "auth/account-exists-with-different-credential") {
        setLocalError("Failed to sign up with GitHub. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "80vh" }}
    >
      <div className="w-100" style={{ maxWidth: "450px" }}>
        <Card>
          <Card.Body>
            <h2 className="text-center mb-4">Sign Up</h2>
            {(localError || error) && (
              <Alert variant="danger">{localError || error}</Alert>
            )}

            {/* Social Login Buttons */}
            <div className="d-grid gap-2">
              <Button
                variant="primary"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="d-flex align-items-center justify-content-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="me-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z" />
                </svg>
                Google
              </Button>

              <Button
                variant="dark"
                onClick={handleGithubSignIn}
                disabled={loading}
                className="d-flex align-items-center justify-content-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="me-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
                </svg>
                GitHub
              </Button>
            </div>

            <div className="text-center mb-3">
              <hr />
              <span className="text-muted small">OR SIGN UP WITH</span>
              <hr />
            </div>

            {/* Email/Password Form */}
            <Form onSubmit={handleEmailSignup} className="mt-4">
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="displayName">
                <Form.Label>
                  Display Name <small>(optional)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>

              <div className="d-grid mb-3">
                <Button variant="success" type="submit" disabled={loading}>
                  {loading ? "Creating Account..." : "Sign Up with Email"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        <div className="w-100 text-center mt-3">
          <small className="text-muted">
            Already have an account?{" "}
            <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`}>
              Sign in
            </Link>
          </small>
        </div>
      </div>
    </Container>
  );
};

export default Signup;
